import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { LogOut, Plus, Trash2, ArrowUpDown, ExternalLink, Mail, Phone } from "lucide-react";

const STATUS_COLORS = {
  new: "bg-[#FF5A36] text-[#F8F9FA]",
  contacted: "bg-[#0B1215] text-[#F8F9FA]",
  completed: "bg-[#E9ECEF] text-[#0B1215] border border-[#0B1215]",
};

function Stat({ label, value, accent }) {
  return (
    <div className={`border-2 border-[#0B1215] p-6 ${accent ? "bg-[#0B1215] text-[#F8F9FA]" : "bg-[#F8F9FA]"}`}>
      <div className="font-mono-label opacity-70">· {label}</div>
      <div className="font-display text-5xl mt-3 tracking-tighter">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, completed: 0 });
  const [loading, setLoading] = useState(true);

  const [tForm, setTForm] = useState({ name: "", location: "", quote: "", rating: 5, avatar_url: "" });
  const [gForm, setGForm] = useState({ title: "", description: "", before_url: "", after_url: "" });
  const [tOpen, setTOpen] = useState(false);
  const [gOpen, setGOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem("rrr_admin_token");
    nav("/admin/login");
  };

  const loadAll = async () => {
    try {
      const [b, t, g, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/testimonials"),
        api.get("/gallery"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setTestimonials(t.data);
      setGallery(g.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("rrr_admin_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load admin data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("rrr_admin_token")) {
      nav("/admin/login");
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked as ${status}.`);
      loadAll();
    } catch {
      toast.error("Update failed.");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/admin/bookings/${id}`);
    toast.success("Deleted.");
    loadAll();
  };

  const sendInvoice = async (b) => {
    const amount = parseFloat(window.prompt(
      `Send Stripe invoice to ${b.email}\n\nAmount in £ (e.g. 265.00):`,
      b.estimated_price ? String(b.estimated_price) : ""
    ));
    if (!amount || amount <= 0) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/invoice`, {
        booking_id: b.id, amount, description: `${b.service} · ${b.property_type || ""}`.trim(),
        origin_url: window.location.origin,
      });
      toast.success(`Invoice sent — £${amount.toFixed(2)} link emailed to ${b.email}`);
      window.prompt("Stripe checkout URL (copy if you also want to text it):", data.url);
      loadAll();
    } catch {
      toast.error("Failed to create invoice. Check Stripe key is configured.");
    }
  };

  const confirmSlot = async (b) => {
    const slot = window.prompt(`Confirm slot for ${b.name}\n\nExample: "Tue 12 Aug · 9am"`, b.preferred_date || "");
    if (!slot) return;
    try {
      await api.post(`/admin/bookings/${b.id}/slot`, { confirmed_slot: slot });
      toast.success(`Slot confirmed — customer notified.`);
      loadAll();
    } catch { toast.error("Failed to confirm slot."); }
  };

  const addProgress = async (b) => {
    const note = window.prompt(`Progress note for ${b.name}\n\n(visible to the customer on their job portal):`);
    if (!note) return;
    const photo_url = window.prompt("Optional photo URL (leave blank to skip):") || "";
    try {
      await api.post(`/admin/bookings/${b.id}/progress`, { note, photo_url });
      toast.success("Progress note posted.");
      loadAll();
    } catch { toast.error("Failed to post update."); }
  };

  const addPhoto = async (b) => {
    const photo_url = window.prompt(`Add a job photo URL for ${b.name}\n\n(host on Imgur/Dropbox or paste your asset URL):`);
    if (!photo_url) return;
    try {
      await api.post(`/admin/bookings/${b.id}/photos`, { photo_url });
      toast.success("Photo added to the job portal.");
      loadAll();
    } catch { toast.error("Failed to add photo."); }
  };

  const copyPortalLink = (b) => {
    const url = `${window.location.origin}/j/${b.view_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Customer job portal link copied.");
  };

  const createTestimonial = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/testimonials", tForm);
      toast.success("Testimonial added.");
      setTForm({ name: "", location: "", quote: "", rating: 5, avatar_url: "" });
      setTOpen(false);
      loadAll();
    } catch {
      toast.error("Failed to add testimonial.");
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Delete testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    loadAll();
  };

  const createGallery = async (e) => {
    e.preventDefault();
    if (!gForm.before_url || !gForm.after_url) {
      toast.error("Need both before and after image URLs.");
      return;
    }
    try {
      await api.post("/admin/gallery", gForm);
      toast.success("Gallery item added.");
      setGForm({ title: "", description: "", before_url: "", after_url: "" });
      setGOpen(false);
      loadAll();
    } catch {
      toast.error("Failed to add item.");
    }
  };

  const deleteGallery = async (id) => {
    if (!window.confirm("Delete gallery item?")) return;
    await api.delete(`/admin/gallery/${id}`);
    loadAll();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="font-mono-label">· LOADING</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      <header className="border-b border-[#0B1215]/10 bg-[#F8F9FA]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/">
              <Logo size="md" />
            </Link>
            <span className="hidden sm:inline font-mono-label text-[#FF5A36]">· ADMIN</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono-label text-[#495057] hover:text-[#0B1215] hidden sm:flex items-center gap-2" data-testid="admin-view-site">
              <ExternalLink size={14} /> View site
            </Link>
            <button
              onClick={logout}
              className="font-mono-label text-[#0B1215] hover:text-[#FF5A36] flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="font-mono-label text-[#FF5A36] mb-3">· OVERVIEW</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tighter">Dashboard.</h1>
          </div>
          <div className="text-sm text-[#495057]">Manage quote requests, reviews & gallery content.</div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat label="Total Requests" value={stats.total} accent />
          <Stat label="New" value={stats.new} />
          <Stat label="Contacted" value={stats.contacted} />
          <Stat label="Completed" value={stats.completed} />
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-[#F8F9FA] border-2 border-[#0B1215] rounded-none h-auto p-0">
            <TabsTrigger
              value="bookings"
              className="rounded-none font-mono-label data-[state=active]:bg-[#0B1215] data-[state=active]:text-[#F8F9FA] px-6 py-3"
              data-testid="tab-bookings"
            >
              Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger
              value="testimonials"
              className="rounded-none font-mono-label data-[state=active]:bg-[#0B1215] data-[state=active]:text-[#F8F9FA] px-6 py-3"
              data-testid="tab-testimonials"
            >
              Testimonials ({testimonials.length})
            </TabsTrigger>
            <TabsTrigger
              value="gallery"
              className="rounded-none font-mono-label data-[state=active]:bg-[#0B1215] data-[state=active]:text-[#F8F9FA] px-6 py-3"
              data-testid="tab-gallery"
            >
              Gallery ({gallery.length})
            </TabsTrigger>
          </TabsList>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-6">
            <div className="border-2 border-[#0B1215] bg-[#F8F9FA] overflow-x-auto" data-testid="bookings-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-[#0B1215] hover:bg-transparent">
                    <TableHead className="font-mono-label text-[#0B1215]">Date</TableHead>
                    <TableHead className="font-mono-label text-[#0B1215]">Name</TableHead>
                    <TableHead className="font-mono-label text-[#0B1215]">Contact</TableHead>
                    <TableHead className="font-mono-label text-[#0B1215]">Service</TableHead>
                    <TableHead className="font-mono-label text-[#0B1215]">Status</TableHead>
                    <TableHead className="font-mono-label text-[#0B1215] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-[#495057]">
                        No quote requests yet. They'll appear here the moment a request is submitted.
                      </TableCell>
                    </TableRow>
                  )}
                  {bookings.map((b) => (
                    <TableRow key={b.id} className="border-b border-[#0B1215]/10" data-testid={`booking-row-${b.id}`}>
                      <TableCell className="text-xs text-[#495057]">
                        {new Date(b.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">{b.name}</div>
                        <div className="text-xs text-[#495057]">{b.property_type || "—"} · {b.preferred_date || "flexible"}</div>
                        {b.estimated_price && (
                          <div className="font-mono-label text-[#FF5A36] mt-1">· EST. from £{b.estimated_price}</div>
                        )}
                        {b.message && <div className="text-xs text-[#495057] mt-1 max-w-xs truncate">{b.message}</div>}
                      </TableCell>
                      <TableCell>
                        <a href={`mailto:${b.email}`} className="text-xs flex items-center gap-1 hover:text-[#FF5A36]"><Mail size={10} /> {b.email}</a>
                        <a href={`tel:${b.phone}`} className="text-xs flex items-center gap-1 hover:text-[#FF5A36] mt-1"><Phone size={10} /> {b.phone}</a>
                      </TableCell>
                      <TableCell className="text-sm">{b.service}</TableCell>
                      <TableCell>
                        <span className={`font-mono-label px-3 py-1 ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                        {b.invoice_status === "paid" && (
                          <div className="font-mono-label text-[#FF5A36] mt-1.5">✓ PAID £{b.invoice_amount?.toFixed(2)}</div>
                        )}
                        {b.invoice_status === "sent" && (
                          <div className="font-mono-label text-[#0B1215] mt-1.5 opacity-70">⏳ INVOICE SENT £{b.invoice_amount?.toFixed(2)}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger className="font-mono-label text-[#495057] hover:text-[#0B1215] flex items-center gap-1 ml-auto" data-testid={`booking-actions-${b.id}`}>
                            <ArrowUpDown size={14} />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-none border-2 border-[#0B1215]">
                            <DropdownMenuItem onClick={() => copyPortalLink(b)} className="font-semibold">
                              🔗 Copy customer portal link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => confirmSlot(b)}>
                              📅 Confirm slot
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => addProgress(b)}>
                              📝 Add progress note
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => addPhoto(b)}>
                              📸 Add job photo
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => sendInvoice(b)} className="text-[#FF5A36] font-semibold">
                              💳 Send Stripe invoice
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(b.id, "new")}>Mark new</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(b.id, "contacted")}>Mark contacted</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => updateStatus(b.id, "completed")}>Mark completed</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => deleteBooking(b.id)}>
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={tOpen} onOpenChange={setTOpen}>
                <DialogTrigger asChild>
                  <button
                    className="inline-flex items-center gap-2 bg-[#0B1215] text-[#F8F9FA] px-5 py-3 font-mono-label hover:bg-[#FF5A36] active:scale-95 transition-all"
                    data-testid="add-testimonial-btn"
                  >
                    <Plus size={14} /> Add review
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-none border-2 border-[#0B1215] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tighter">Add testimonial</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createTestimonial} className="space-y-4 mt-4">
                    <div>
                      <Label className="font-mono-label">Name</Label>
                      <Input required value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" data-testid="t-name" />
                    </div>
                    <div>
                      <Label className="font-mono-label">Location</Label>
                      <Input required value={tForm.location} onChange={(e) => setTForm({ ...tForm, location: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" data-testid="t-location" />
                    </div>
                    <div>
                      <Label className="font-mono-label">Quote</Label>
                      <Textarea required rows={4} value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} className="rounded-none border-[#0B1215] mt-2" data-testid="t-quote" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="font-mono-label">Rating (1-5)</Label>
                        <Input type="number" min={1} max={5} value={tForm.rating} onChange={(e) => setTForm({ ...tForm, rating: parseInt(e.target.value || "5", 10) })} className="rounded-none border-[#0B1215] mt-2 h-11" />
                      </div>
                      <div>
                        <Label className="font-mono-label">Avatar URL</Label>
                        <Input value={tForm.avatar_url} onChange={(e) => setTForm({ ...tForm, avatar_url: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" />
                      </div>
                    </div>
                    <button type="submit" className="w-full bg-[#FF5A36] text-[#F8F9FA] px-6 py-3 font-mono-label active:scale-95" data-testid="t-submit">
                      Save review
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="border-2 border-[#0B1215] p-6 bg-[#F8F9FA] flex flex-col gap-3" data-testid={`testimonial-admin-${t.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold">{t.name}</div>
                      <div className="font-mono-label text-[#495057]">{t.location} · {t.rating}★</div>
                    </div>
                    <button onClick={() => deleteTestimonial(t.id)} className="text-red-600 hover:text-red-800" data-testid={`del-testimonial-${t.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <p className="text-sm leading-relaxed">"{t.quote}"</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={gOpen} onOpenChange={setGOpen}>
                <DialogTrigger asChild>
                  <button
                    className="inline-flex items-center gap-2 bg-[#0B1215] text-[#F8F9FA] px-5 py-3 font-mono-label hover:bg-[#FF5A36] active:scale-95 transition-all"
                    data-testid="add-gallery-btn"
                  >
                    <Plus size={14} /> Add before/after
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-none border-2 border-[#0B1215] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tighter">Add gallery item</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={createGallery} className="space-y-4 mt-4">
                    <div>
                      <Label className="font-mono-label">Title</Label>
                      <Input required value={gForm.title} onChange={(e) => setGForm({ ...gForm, title: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" data-testid="g-title" />
                    </div>
                    <div>
                      <Label className="font-mono-label">Description</Label>
                      <Input value={gForm.description} onChange={(e) => setGForm({ ...gForm, description: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" />
                    </div>
                    <div>
                      <Label className="font-mono-label">Before image URL</Label>
                      <Input required value={gForm.before_url} onChange={(e) => setGForm({ ...gForm, before_url: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" data-testid="g-before" />
                    </div>
                    <div>
                      <Label className="font-mono-label">After image URL</Label>
                      <Input required value={gForm.after_url} onChange={(e) => setGForm({ ...gForm, after_url: e.target.value })} className="rounded-none border-[#0B1215] mt-2 h-11" data-testid="g-after" />
                    </div>
                    <button type="submit" className="w-full bg-[#FF5A36] text-[#F8F9FA] px-6 py-3 font-mono-label active:scale-95" data-testid="g-submit">
                      Save item
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="border-2 border-[#0B1215] p-4 bg-[#F8F9FA]" data-testid={`gallery-admin-${g.id}`}>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <img src={g.before_url} alt="before" className="aspect-square object-cover w-full" />
                    <img src={g.after_url} alt="after" className="aspect-square object-cover w-full" />
                  </div>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-semibold">{g.title}</div>
                      {g.description && <div className="text-xs text-[#495057] mt-1">{g.description}</div>}
                    </div>
                    <button onClick={() => deleteGallery(g.id)} className="text-red-600 hover:text-red-800" data-testid={`del-gallery-${g.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
